import type {
  AdjustmentInput,
  AnonymityState,
  ApiErrorBody,
  AssignBlockInput,
  Block,
  BlockContributor,
  BlockApproval,
  BlockApprovalsListResponse,
  BlockDetail,
  BlockImportCommitResponse,
  BlockImportInput,
  BlockImportPreview,
  BlockSeason,
  BlockSeasonsListResponse,
  BlocksListResponse,
  CoinUploadInput,
  CoinUploadResponse,
  ContributorShareInput,
  CreateBlockSeasonInput,
  CreateNoteInput,
  CreatePillarInput,
  CreateSeasonInput,
  CreateSubBlockInput,
  CreateSubBlockResult,
  CreateTopBlockInput,
  Department,
  DepartmentsListResponse,
  EarningBand,
  EarningCurve,
  GumballsMeResponse,
  LedgerEntry,
  LedgerListResponse,
  LogoutResponse,
  MembersResponse,
  MeResponse,
  Note,
  NotesListResponse,
  PeopleResponse,
  Pillar,
  PillarsListResponse,
  Position,
  PositionInput,
  PositionsListResponse,
  RecipientsResponse,
  Season,
  SeasonsListResponse,
  SpendInput,
  SubmitCompletionResult,
  ThemeSetting,
  ThemesResponse,
  UpdateBlockDetailsInput,
  UpdatePillarInput,
  UpdateProgressInput,
  UpdateScoreInput,
  VisionView
} from "./types";

const csrfHeader = "X-CSRF-Token";
const idempotencyHeader = "Idempotency-Key";
const jsonContentType = "application/json";
const unsafeMethods = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly body?: ApiErrorBody;

  constructor(status: number, body?: ApiErrorBody) {
    super(body?.message ?? body?.error ?? `Request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = body?.code;
    this.body = body;
  }
}

interface RequestOptions extends RequestInit {
  retryOnCSRF?: boolean;
  idempotencyKey?: string;
}

export class ApiClient {
  private csrfToken = "";

  async getMe(): Promise<MeResponse> {
    const me = await this.request<MeResponse>("/api/me", { method: "GET" });
    this.setCSRFToken(me.csrf_token);
    return me;
  }

  async logout(): Promise<LogoutResponse> {
    return this.request<LogoutResponse>("/auth/logout", { method: "POST" });
  }

  // Gumballs reads ----------------------------------------------------

  async getGumballsMe(seasonID?: string): Promise<GumballsMeResponse> {
    const path = seasonID !== undefined && seasonID !== ""
      ? `/api/gumballs/me?season_id=${encodeURIComponent(seasonID)}`
      : "/api/gumballs/me";
    return this.request<GumballsMeResponse>(path, { method: "GET" });
  }

  async listLedger(params: {
    seasonID?: string;
    userID?: string;
    cursor?: string;
    limit?: number;
  } = {}): Promise<LedgerListResponse> {
    const query = new URLSearchParams();
    if (params.seasonID !== undefined && params.seasonID !== "") {
      query.set("season_id", params.seasonID);
    }
    if (params.userID !== undefined && params.userID !== "") {
      query.set("user_id", params.userID);
    }
    if (params.cursor !== undefined && params.cursor !== "") {
      query.set("cursor", params.cursor);
    }
    if (params.limit !== undefined) {
      query.set("limit", String(params.limit));
    }
    const suffix = query.toString();
    const path = suffix === "" ? "/api/gumballs/ledger" : `/api/gumballs/ledger?${suffix}`;
    return this.request<LedgerListResponse>(path, { method: "GET" });
  }

  async listRecipients(): Promise<RecipientsResponse> {
    return this.request<RecipientsResponse>("/api/gumballs/recipients", { method: "GET" });
  }

  async listAdjustmentTargets(): Promise<RecipientsResponse> {
    return this.request<RecipientsResponse>("/api/gumballs/adjustment-targets", { method: "GET" });
  }

  async listMembers(): Promise<MembersResponse> {
    return this.request<MembersResponse>("/api/members", { method: "GET" });
  }

  // Notes -------------------------------------------------------------

  async listNotes(params: { cursor?: string; limit?: number } = {}): Promise<NotesListResponse> {
    const query = new URLSearchParams();
    if (params.cursor !== undefined && params.cursor !== "") {
      query.set("cursor", params.cursor);
    }
    if (params.limit !== undefined) {
      query.set("limit", String(params.limit));
    }
    const suffix = query.toString();
    const path = suffix === "" ? "/api/notes" : `/api/notes?${suffix}`;
    return this.request<NotesListResponse>(path, { method: "GET" });
  }

  async createNote(input: CreateNoteInput): Promise<Note> {
    return this.request<Note>("/api/notes", {
      method: "POST",
      body: JSON.stringify(input)
    });
  }

  async updateNote(id: string, input: CreateNoteInput): Promise<Note> {
    return this.request<Note>(`/api/notes/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input)
    });
  }

  async deleteNote(id: string): Promise<void> {
    await this.request<void>(`/api/notes/${encodeURIComponent(id)}`, { method: "DELETE" });
  }

  // Hiring --------------------------------------------------------------

  async listPositions(params: { status?: string; cursor?: string } = {}): Promise<PositionsListResponse> {
    const query = new URLSearchParams();
    if (params.status !== undefined && params.status !== "") {
      query.set("status", params.status);
    }
    if (params.cursor !== undefined && params.cursor !== "") {
      query.set("cursor", params.cursor);
    }
    const suffix = query.toString();
    const path = suffix === "" ? "/api/positions" : `/api/positions?${suffix}`;
    return this.request<PositionsListResponse>(path, { method: "GET" });
  }

  async createPosition(input: PositionInput, idempotencyKey: string): Promise<Position> {
    return this.request<Position>("/api/positions", {
      method: "POST",
      body: JSON.stringify(input),
      idempotencyKey
    });
  }

  // Partial update: only the keys present in `input` change; `videos`
  // present means full replace of the video set.
  async updatePosition(id: string, input: Partial<PositionInput>, idempotencyKey: string): Promise<Position> {
    return this.request<Position>(`/api/positions/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
      idempotencyKey
    });
  }

  async deletePosition(id: string, idempotencyKey: string): Promise<{ status: string }> {
    return this.request<{ status: string }>(`/api/positions/${encodeURIComponent(id)}`, {
      method: "DELETE",
      idempotencyKey
    });
  }

  async respondToPosition(id: string, message: string, idempotencyKey: string): Promise<{ status: string }> {
    return this.request<{ status: string }>(`/api/positions/${encodeURIComponent(id)}/respond`, {
      method: "POST",
      body: JSON.stringify({ message }),
      idempotencyKey
    });
  }

  async listDepartments(): Promise<DepartmentsListResponse> {
    return this.request<DepartmentsListResponse>("/api/departments", { method: "GET" });
  }

  async createDepartment(name: string, idempotencyKey: string): Promise<Department> {
    return this.request<Department>("/api/departments", {
      method: "POST",
      body: JSON.stringify({ name }),
      idempotencyKey
    });
  }

  async updateDepartment(id: string, name: string, idempotencyKey: string): Promise<Department> {
    return this.request<Department>(`/api/departments/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
      idempotencyKey
    });
  }

  async deleteDepartment(id: string, idempotencyKey: string): Promise<{ status: string }> {
    return this.request<{ status: string }>(`/api/departments/${encodeURIComponent(id)}`, {
      method: "DELETE",
      idempotencyKey
    });
  }

  async listThemes(): Promise<ThemesResponse> {
    return this.request<ThemesResponse>("/api/themes", { method: "GET" });
  }

  async setThemeActive(themeID: string, isActive: boolean): Promise<ThemeSetting> {
    return this.request<ThemeSetting>(
      `/api/themes/${encodeURIComponent(themeID)}`,
      {
        method: "PATCH",
        headers: { "Content-Type": jsonContentType },
        body: JSON.stringify({ is_active: isActive })
      }
    );
  }

  async changeMemberRole(userID: string, role: string): Promise<{ id: string; role: string }> {
    return this.request<{ id: string; role: string }>(
      `/api/members/${encodeURIComponent(userID)}/role`,
      {
        method: "PATCH",
        headers: { "Content-Type": jsonContentType },
        body: JSON.stringify({ role })
      }
    );
  }

  async deleteMember(userID: string): Promise<{ id: string; status: string }> {
    return this.request<{ id: string; status: string }>(
      `/api/members/${encodeURIComponent(userID)}`,
      { method: "DELETE" }
    );
  }

  async listSeasons(params: { status?: string; cursor?: string; limit?: number } = {}): Promise<SeasonsListResponse> {
    const query = new URLSearchParams();
    if (params.status !== undefined && params.status !== "") {
      query.set("status", params.status);
    }
    if (params.cursor !== undefined && params.cursor !== "") {
      query.set("cursor", params.cursor);
    }
    if (params.limit !== undefined) {
      query.set("limit", String(params.limit));
    }
    const suffix = query.toString();
    const path = suffix === "" ? "/api/gumballs/seasons" : `/api/gumballs/seasons?${suffix}`;
    return this.request<SeasonsListResponse>(path, { method: "GET" });
  }

  // Blocks ------------------------------------------------------------

  async listBlocks(
    params: {
      season_id?: string;
      pillar_id?: string;
      level?: string;
      parent_id?: string;
      status?: string;
      // mine restricts the list to blocks the caller relates to (assignee, exec
      // owner, or contributor); the server injects the caller's own id.
      mine?: boolean;
      cursor?: string;
      limit?: number;
    } = {}
  ): Promise<BlocksListResponse> {
    const query = new URLSearchParams();
    const setIf = (key: string, value?: string) => {
      if (value !== undefined && value !== "") {
        query.set(key, value);
      }
    };
    setIf("season_id", params.season_id);
    setIf("pillar_id", params.pillar_id);
    setIf("level", params.level);
    setIf("parent_id", params.parent_id);
    setIf("status", params.status);
    setIf("cursor", params.cursor);
    if (params.mine === true) {
      query.set("mine", "true");
    }
    if (params.limit !== undefined) {
      query.set("limit", String(params.limit));
    }
    const suffix = query.toString();
    const path = suffix === "" ? "/api/blocks" : `/api/blocks?${suffix}`;
    return this.request<BlocksListResponse>(path, { method: "GET" });
  }

  async getBlock(id: string): Promise<BlockDetail> {
    const response = await this.request<{ block: BlockDetail }>(
      `/api/blocks/${encodeURIComponent(id)}`,
      { method: "GET" }
    );
    return response.block;
  }

  async createTopBlock(input: CreateTopBlockInput, idempotencyKey: string): Promise<Block> {
    const response = await this.request<{ block: Block }>("/api/blocks", {
      method: "POST",
      body: JSON.stringify(input),
      idempotencyKey
    });
    return response.block;
  }

  async listPillars(): Promise<Pillar[]> {
    const response = await this.request<PillarsListResponse>("/api/pillars", { method: "GET" });
    return response.pillars;
  }

  async createPillar(input: CreatePillarInput, idempotencyKey: string): Promise<Pillar> {
    const response = await this.request<{ pillar: Pillar }>("/api/pillars", {
      method: "POST",
      body: JSON.stringify(input),
      idempotencyKey
    });
    return response.pillar;
  }

  async updatePillar(id: string, input: UpdatePillarInput, idempotencyKey: string): Promise<Pillar> {
    const response = await this.request<{ pillar: Pillar }>(
      `/api/pillars/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify(input),
        idempotencyKey
      }
    );
    return response.pillar;
  }

  async assignBlock(id: string, input: AssignBlockInput, idempotencyKey: string): Promise<Block> {
    const response = await this.request<{ block: Block }>(
      `/api/blocks/${encodeURIComponent(id)}/assign`,
      {
        method: "POST",
        body: JSON.stringify(input),
        idempotencyKey
      }
    );
    return response.block;
  }

  // setVisionColor sets the current user's Vision color — the tint the sculpture
  // renders their whole territory in — to a palette hex, or clears it back to the
  // default with an empty string. Returns the resolved color (null when cleared).
  async setVisionColor(color: string, idempotencyKey: string): Promise<string | null> {
    const response = await this.request<{ color: string | null }>(
      `/api/blocks/vision/color`,
      {
        method: "PUT",
        body: JSON.stringify({ color }),
        idempotencyKey
      }
    );
    return response.color;
  }

  // setSeasonShape commits the Phoenix shape everyone sees for a season to a
  // registry slug (or "" to clear back to the app default). Admin-gated by
  // blocks:vision:shape server-side. Returns the persisted slug.
  async setSeasonShape(seasonId: string, shapeSlug: string, idempotencyKey: string): Promise<string> {
    const response = await this.request<{ shape_slug: string }>(
      `/api/blocks/seasons/${encodeURIComponent(seasonId)}/shape`,
      {
        method: "PUT",
        body: JSON.stringify({ shape_slug: shapeSlug }),
        idempotencyKey
      }
    );
    return response.shape_slug;
  }

  async createSubBlock(
    parentID: string,
    input: CreateSubBlockInput,
    idempotencyKey: string
  ): Promise<CreateSubBlockResult> {
    return this.request<CreateSubBlockResult>(`/api/blocks/${encodeURIComponent(parentID)}/children`, {
      method: "POST",
      body: JSON.stringify(input),
      idempotencyKey
    });
  }

  async updateBlockScore(id: string, input: UpdateScoreInput, idempotencyKey: string): Promise<Block> {
    const response = await this.request<{ block: Block }>(
      `/api/blocks/${encodeURIComponent(id)}/score`,
      {
        method: "PATCH",
        body: JSON.stringify(input),
        idempotencyKey
      }
    );
    return response.block;
  }

  async updateBlockDetails(id: string, input: UpdateBlockDetailsInput, idempotencyKey: string): Promise<Block> {
    const response = await this.request<{ block: Block }>(
      `/api/blocks/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify(input),
        idempotencyKey
      }
    );
    return response.block;
  }

  async updateBlockNotes(id: string, notes: string, idempotencyKey: string): Promise<string | null> {
    const response = await this.request<{ notes: string | null }>(
      `/api/blocks/${encodeURIComponent(id)}/notes`,
      {
        method: "PATCH",
        body: JSON.stringify({ notes }),
        idempotencyKey
      }
    );
    return response.notes;
  }

  async updateBlockProgress(id: string, input: UpdateProgressInput, idempotencyKey: string): Promise<Block> {
    const response = await this.request<{ block: Block }>(
      `/api/blocks/${encodeURIComponent(id)}/progress`,
      {
        method: "PATCH",
        body: JSON.stringify(input),
        idempotencyKey
      }
    );
    return response.block;
  }

  async submitBlockCompletion(id: string, idempotencyKey: string): Promise<SubmitCompletionResult> {
    return this.request<SubmitCompletionResult>(`/api/blocks/${encodeURIComponent(id)}/submit-completion`, {
      method: "POST",
      idempotencyKey
    });
  }

  async listBlockApprovals(
    params: { status?: string; cursor?: string; limit?: number } = {}
  ): Promise<BlockApprovalsListResponse> {
    const query = new URLSearchParams();
    if (params.status !== undefined && params.status !== "") {
      query.set("status", params.status);
    }
    if (params.cursor !== undefined && params.cursor !== "") {
      query.set("cursor", params.cursor);
    }
    if (params.limit !== undefined) {
      query.set("limit", String(params.limit));
    }
    const suffix = query.toString();
    const path = suffix === "" ? "/api/blocks/approvals" : `/api/blocks/approvals?${suffix}`;
    return this.request<BlockApprovalsListResponse>(path, { method: "GET" });
  }

  async approveBlockApproval(id: string, idempotencyKey: string): Promise<BlockApproval> {
    const response = await this.request<{ approval: BlockApproval }>(
      `/api/approvals/${encodeURIComponent(id)}/approve`,
      { method: "POST", idempotencyKey }
    );
    return response.approval;
  }

  async rejectBlockApproval(id: string, reason: string, idempotencyKey: string): Promise<BlockApproval> {
    const response = await this.request<{ approval: BlockApproval }>(
      `/api/approvals/${encodeURIComponent(id)}/reject`,
      { method: "POST", body: JSON.stringify({ reason }), idempotencyKey }
    );
    return response.approval;
  }

  async setBlockContributors(
    id: string,
    shares: ContributorShareInput[],
    idempotencyKey: string
  ): Promise<BlockContributor[]> {
    const response = await this.request<{ contributors: BlockContributor[] }>(
      `/api/blocks/${encodeURIComponent(id)}/contributors`,
      {
        method: "PUT",
        body: JSON.stringify({ shares }),
        idempotencyKey
      }
    );
    return response.contributors;
  }

  async getEarningCurve(): Promise<EarningCurve> {
    const response = await this.request<{ earning_curve: EarningCurve }>("/api/blocks/earning-curve", {
      method: "GET"
    });
    return response.earning_curve;
  }

  async putEarningCurve(bands: EarningBand[], idempotencyKey: string): Promise<EarningCurve> {
    const response = await this.request<{ earning_curve: EarningCurve }>("/api/blocks/earning-curve", {
      method: "PUT",
      body: JSON.stringify({ bands }),
      idempotencyKey
    });
    return response.earning_curve;
  }

  // The People aggregate pivots a season's blocks by person: earned total,
  // assigned-vs-complete counts, and each person's blocks tagged by role. The
  // optional team filter narrows which blocks are considered (server-side).
  async listBlockPeople(params: { season_id?: string; team?: string } = {}): Promise<PeopleResponse> {
    const query = new URLSearchParams();
    if (params.season_id !== undefined && params.season_id !== "") {
      query.set("season_id", params.season_id);
    }
    if (params.team !== undefined && params.team !== "") {
      query.set("team", params.team);
    }
    const suffix = query.toString();
    const path = suffix === "" ? "/api/blocks/people" : `/api/blocks/people?${suffix}`;
    return this.request<PeopleResponse>(path, { method: "GET" });
  }

  async getVision(seasonID?: string): Promise<VisionView> {
    const path = visionPath("/api/blocks/vision", seasonID);
    const response = await this.request<{ vision: VisionView }>(path, { method: "GET" });
    return response.vision;
  }

  // Admin-only "fully realized" Phoenix preview (every region at build
  // fraction 1.0). Server-gated by blocks:vision:preview; non-admins get 403.
  async getVisionFull(seasonID?: string): Promise<VisionView> {
    const path = visionPath("/api/blocks/vision/full", seasonID);
    const response = await this.request<{ vision: VisionView }>(path, { method: "GET" });
    return response.vision;
  }

  // Block seasons (a separate timeline from gumball seasons). List and all
  // mutations are leadership-only (blocks:seasons:manage), gated server-side.
  async listBlockSeasons(params: { status?: string; cursor?: string; limit?: number } = {}): Promise<BlockSeasonsListResponse> {
    const query = new URLSearchParams();
    if (params.status !== undefined && params.status !== "") {
      query.set("status", params.status);
    }
    if (params.cursor !== undefined && params.cursor !== "") {
      query.set("cursor", params.cursor);
    }
    if (params.limit !== undefined) {
      query.set("limit", String(params.limit));
    }
    const suffix = query.toString();
    const path = suffix === "" ? "/api/blocks/seasons" : `/api/blocks/seasons?${suffix}`;
    return this.request<BlockSeasonsListResponse>(path, { method: "GET" });
  }

  async createBlockSeason(input: CreateBlockSeasonInput, idempotencyKey: string): Promise<BlockSeason> {
    return this.request<BlockSeason>("/api/blocks/seasons", {
      method: "POST",
      body: JSON.stringify(input),
      idempotencyKey
    });
  }

  async activateBlockSeason(id: string, idempotencyKey: string): Promise<BlockSeason> {
    return this.request<BlockSeason>(`/api/blocks/seasons/${encodeURIComponent(id)}/activate`, {
      method: "POST",
      idempotencyKey
    });
  }

  async closeBlockSeason(id: string, idempotencyKey: string): Promise<BlockSeason> {
    return this.request<BlockSeason>(`/api/blocks/seasons/${encodeURIComponent(id)}/close`, {
      method: "POST",
      idempotencyKey
    });
  }

  // Mass block upload. The preview is a dry run that writes nothing; the commit
  // re-validates and creates everything in one transaction, or nothing.
  async previewBlockImport(input: BlockImportInput): Promise<BlockImportPreview> {
    return this.request<BlockImportPreview>("/api/blocks/import/preview", {
      method: "POST",
      body: JSON.stringify(input)
    });
  }

  async commitBlockImport(input: BlockImportInput, idempotencyKey: string): Promise<BlockImportCommitResponse> {
    return this.request<BlockImportCommitResponse>("/api/blocks/import", {
      method: "POST",
      body: JSON.stringify(input),
      idempotencyKey
    });
  }

  // Gumballs writes ---------------------------------------------------

  async spend(input: SpendInput, idempotencyKey: string): Promise<LedgerEntry> {
    return this.request<LedgerEntry>("/api/gumballs/spend", {
      method: "POST",
      body: JSON.stringify(input),
      idempotencyKey
    });
  }

  async adjust(input: AdjustmentInput, idempotencyKey: string): Promise<LedgerEntry> {
    return this.request<LedgerEntry>("/api/gumballs/adjustments", {
      method: "POST",
      body: JSON.stringify(input),
      idempotencyKey
    });
  }

  async uploadCoins(input: CoinUploadInput, idempotencyKey: string): Promise<CoinUploadResponse> {
    return this.request<CoinUploadResponse>("/api/gumballs/coin-uploads", {
      method: "POST",
      body: JSON.stringify(input),
      idempotencyKey
    });
  }

  async createSeason(input: CreateSeasonInput): Promise<Season> {
    return this.request<Season>("/api/gumballs/seasons", {
      method: "POST",
      body: JSON.stringify(input)
    });
  }

  async activateSeason(id: string): Promise<Season> {
    return this.request<Season>(`/api/gumballs/seasons/${encodeURIComponent(id)}/activate`, {
      method: "POST"
    });
  }

  async closeSeason(id: string, carryIntoSeasonID: string): Promise<Season> {
    return this.request<Season>(`/api/gumballs/seasons/${encodeURIComponent(id)}/close`, {
      method: "POST",
      body: JSON.stringify({ carry_into_season_id: carryIntoSeasonID })
    });
  }

  async setAnonymity(seasonID: string, anonymous: boolean): Promise<AnonymityState> {
    return this.request<AnonymityState>(
      `/api/gumballs/seasons/${encodeURIComponent(seasonID)}/anonymity`,
      {
        method: "POST",
        body: JSON.stringify({ anonymous })
      }
    );
  }

  // -------------------------------------------------------------------

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const method = (options.method ?? "GET").toUpperCase();
    const retryOnCSRF = options.retryOnCSRF ?? true;
    const headers = new Headers(options.headers);

    headers.set("Accept", jsonContentType);
    if (options.body !== undefined && !(options.body instanceof FormData) && !headers.has("Content-Type")) {
      headers.set("Content-Type", jsonContentType);
    }
    if (unsafeMethods.has(method) && this.csrfToken !== "") {
      headers.set(csrfHeader, this.csrfToken);
    }
    if (options.idempotencyKey !== undefined && options.idempotencyKey !== "") {
      headers.set(idempotencyHeader, options.idempotencyKey);
    }

    const response = await fetch(path, {
      ...options,
      method,
      credentials: "same-origin",
      headers
    });

    const rotatedToken = response.headers.get(csrfHeader);
    if (rotatedToken !== null && rotatedToken !== "") {
      this.setCSRFToken(rotatedToken);
    }

    const body = await parseResponseBody(response);
    if (!response.ok) {
      const errorBody = body as ApiErrorBody | undefined;
      if (
        response.status === 403 &&
        errorBody?.code === "csrf_invalid" &&
        unsafeMethods.has(method) &&
        retryOnCSRF
      ) {
        await this.refreshCSRFToken();
        return this.request<T>(path, { ...options, method, retryOnCSRF: false });
      }
      throw new ApiError(response.status, errorBody);
    }

    return body as T;
  }

  private async refreshCSRFToken(): Promise<void> {
    const response = await fetch("/api/me", {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: jsonContentType
      }
    });

    const body = await parseResponseBody(response);
    if (!response.ok) {
      throw new ApiError(response.status, body as ApiErrorBody | undefined);
    }

    const me = body as MeResponse;
    this.setCSRFToken(me.csrf_token);
  }

  private setCSRFToken(token: string): void {
    this.csrfToken = token;
  }
}

function visionPath(base: string, seasonID?: string): string {
  if (seasonID === undefined || seasonID === "") {
    return base;
  }
  return `${base}?season_id=${encodeURIComponent(seasonID)}`;
}

async function parseResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return undefined;
  }

  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.includes(jsonContentType)) {
    return undefined;
  }

  return response.json();
}

export const apiClient = new ApiClient();
